import {
  asArray,
  asBoolean,
  asNumber,
  asString,
  isObject,
} from "./model-utils";
import {
  enumValue,
  DisplayPriceOption,
  InvoiceLogoOption,
  InvoiceTemplate,
  InvoiceType,
  SearchBarOption,
} from "./enums";

export class AccessPointSettings {
  [key: string]: any;
  constructor(json: any = {}) {
    Object.assign(this, json);
    this.allowGuest = asBoolean(json.allowGuest);
    this.enableGuestAuthorisation = asBoolean(json.enableGuestAuthorisation);
    this.allowMembers = asBoolean(json.allowMembers);
    this.enableMemberAuthorisation = asBoolean(json.enableMemberAuthorisation);
    this.sameDeviceAuthorisation = asBoolean(json.sameDeviceAuthorisation);
    this.enableMemberCheckIn = asBoolean(json.enableMemberCheckIn);
    this.onlyMembersWithMembership = asBoolean(json.onlyMembersWithMembership);
    this.enableMemberAccessLimit = asBoolean(json.enableMemberAccessLimit);
    this.enableWalkInGuest = asBoolean(json.enableWalkInGuest);
    this.enableSelfie = asBoolean(json.enableSelfie);
    this.enableQrCode = asBoolean(json.enableQrCode);
    this.sessionTime = asBoolean(json.sessionTime);
    this.openTime = asString(json.openTime);
    this.closeTime = asString(json.closeTime);
    this.enableAccessNotification = asBoolean(json.enableAccessNotification);
    this.enableProductInvoice = asBoolean(json.enableProductInvoice);
    this.disableStoreFrontOrderReview = asBoolean(
      json.disableStoreFrontOrderReview,
    );
    this.storefrontMarkUpPercentage = asNumber(json.storefrontMarkUpPercentage);
    this.storePaymentMessage = asString(json.storePaymentMessage);
    this.inStorePurchase = asBoolean(json.inStorePurchase);
    this.pickUpPurchase = asBoolean(json.pickUpPurchase);
    this.deliveryPurchase = asBoolean(json.deliveryPurchase);
    this.pickUpLocation = asString(json.pickUpLocation);
    this.invoiceBusinessName = asString(json.invoiceBusinessName);
    this.invoiceLogoUrl = asString(json.invoiceLogoUrl);
    this.invoiceDuration = asNumber(json.invoiceDuration);
    this.invoiceVatPercentage = asNumber(json.invoiceVatPercentage);
    this.storefrontPublic = asBoolean(json.storefrontPublic);
    this.enforceSupportingDocuments = asBoolean(
      json.enforceSupportingDocuments,
    );
    this.enforcePaymentSupportingDocuments = asBoolean(
      json.enforcePaymentSupportingDocuments,
    );
    this.enforcePurchaseInventory = asBoolean(json.enforcePurchaseInventory);
    this.sendNotificationToNonActiveMembers = asBoolean(
      json.sendNotificationToNonActiveMembers,
    );
    this.storeFrontNotification = asBoolean(json.storeFrontNotification);
    this.backStoreNotification = asBoolean(json.backStoreNotification);
    this.vatIncludedInProductPrice = asBoolean(json.vatIncludedInProductPrice);
    this.vatAmountRound = asBoolean(json.vatAmountRound);
    this.vatExcludeFromDelivery = asBoolean(json.vatExcludeFromDelivery);
    this.vatOnlyCalculatedOnProfit = asBoolean(json.vatOnlyCalculatedOnProfit);
    this.markUpPriceByVat = asBoolean(json.markUpPriceByVat);
    this.autoConfirmStoreOrders = asBoolean(json.autoConfirmStoreOrders);
    this.showOutOfStockInStoreFront = asBoolean(
      json.showOutOfStockInStoreFront,
    );
    this.disableStoreFrontPrice = asBoolean(json.disableStoreFrontPrice);
    this.enableOrderPlacementWithoutPayment = asBoolean(
      json.enableOrderPlacementWithoutPayment,
    );
    this.enableStoreOfflinePayment = asBoolean(json.enableStoreOfflinePayment);
    this.enableRouteDeliveryAmountEstimate = asBoolean(
      json.enableRouteDeliveryAmountEstimate,
    );
    this.applyRouteEstimateToOrder = asBoolean(json.applyRouteEstimateToOrder);
    this.paymentWindowInMinutes = asNumber(json.paymentWindowInMinutes);
    this.invoiceNote = asString(json.invoiceNote);
    this.invoiceLogoOption = enumValue(
      InvoiceLogoOption,
      json.invoiceLogoOption,
    );
    this.timezone = asString(json.timezone);
    this.sId = asString(json._id ?? json.sId);
    this.externalEmail = asString(json.externalEmail);
    this.storeFrontDisplayPrice = enumValue(
      DisplayPriceOption,
      json.storeFrontDisplayPrice,
    );
    this.backStoreSearchBar = enumValue(
      SearchBarOption,
      json.backStoreSearchBar,
    );
    this.memberships = asArray(json.memberships);
    this.accessLimits = asArray(json.accessLimits);
    this.invoiceType = enumValue(InvoiceType, json.invoiceType);
    this.invoiceTemplate = enumValue(InvoiceTemplate, json.invoiceTemplate);
    this.enableLocationTracking = asBoolean(json.enableLocationTracking);
    this.enableProductItemTracking = asBoolean(json.enableProductItemTracking);
    this.lockItemRemovalWithPin = asBoolean(json.lockItemRemovalWithPin);
    this.allowNegativeStock = asBoolean(json.allowNegativeStock);
    this.enableReferenceSku = asBoolean(json.enableReferenceSku);
    this.enforcePrintOnceOnOrder = asBoolean(json.enforcePrintOnceOnOrder);
    this.useCustomInvoicePrefix = asBoolean(json.useCustomInvoicePrefix);
    this.customInvoicePrefix = asString(json.customInvoicePrefix);
    this.customInvoiceDelimiter = asString(json.customInvoiceDelimiter);
    this.removePhoneNoOnInvoice = asBoolean(json.removePhoneNoOnInvoice);
    this.removeEmailOnInvoice = asBoolean(json.removeEmailOnInvoice);
    this.removeAddressOnInvoice = asBoolean(json.removeAddressOnInvoice);
    this.removeInvoiceDate = asBoolean(json.removeInvoiceDate);
    this.enableProductCurrencySwitch = asBoolean(
      json.enableProductCurrencySwitch,
    );
    this.enableInvoiceAmountInWords = asBoolean(
      json.enableInvoiceAmountInWords,
    );
    this.removeBusinessName = asBoolean(json.removeBusinessName);
    this.inventoryScopedProducts = asBoolean(json.inventoryScopedProducts);
    this.logisticsScopedProducts = asBoolean(json.logisticsScopedProducts);
    this.noOfflogisticsProductsPerOrder = asNumber(
      json.noOfflogisticsProductsPerOrder,
    );
    this.disableOrderGuestMode = asBoolean(json.disableOrderGuestMode);
    this.enforceVerifiedBuyers = asBoolean(json.enforceVerifiedBuyers);
    this.removeDueDate = asBoolean(json.removeDueDate);
    this.showItemTrackingOnInvoice = asBoolean(json.showItemTrackingOnInvoice);
    this.invoiceEmails = asString(json.invoiceEmails);
    this.invoicePhoneNos = asString(json.invoicePhoneNos);
    this.enableReferenceDataPoint = asBoolean(json.enableReferenceDataPoint);
    this.useProductPriceOnImageUpload = asBoolean(
      json.useProductPriceOnImageUpload,
    );
    this.defaultRenderMode = asString(json.defaultRenderMode);
    this.offlineOrderThresholdHours = asNumber(json.offlineOrderThresholdHours);
    this.enableOfflineActivity = asBoolean(json.enableOfflineActivity);
    this.orderSyncWindowInDays = asNumber(json.orderSyncWindowInDays);
    this.listOnMarketplace = asBoolean(json.listOnMarketplace);
    this.activateSelectedDeviceForOffline = asBoolean(
      json.activateSelectedDeviceForOffline,
    );
    this.restrictOrderCancellation = asBoolean(json.restrictOrderCancellation);
    this.autoCancelUnpaidOrders = asBoolean(json.autoCancelUnpaidOrders);
  }

  static fromJson(json: any) {
    return isObject(json) ? new AccessPointSettings(json) : json;
  }
}
